import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { requirementsApi } from '../services/api';
import { iamApi } from '../services/iamApi';
import { saveWithConflictPrompt } from '../utils/conflict';
import { itemIdFromHash, nextItemUrl, selectedItemFromUrl } from './itemUrl';
import { noteItemProject } from '../services/authToken';

/**
 * Helper function to find an item by ID in the tree structure
 * @param {Array} items - The tree items to search through
 * @param {string} id - The ID to find
 * @returns {Object|null} The found item or null
 */
export const findItemById = (items, id) => {
  if (!items || !id) return null;

  for (const item of items) {
    if (item.id === id) return item;
    if (item.children && item.children.length > 0) {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Helper function to find parent IDs of an item in the tree structure
 * @param {Array} items - The tree items to search through
 * @param {string} id - The ID of the item to find parents for
 * @param {Array} parents - Accumulator for parent IDs
 * @returns {Array} List of parent IDs
 */
export const findParentIds = (items, id, parents = []) => {
  if (!items || !id) return [];

  for (const item of items) {
    if (item.id === id) return parents;
    if (item.children && item.children.length > 0) {
      const result = findParentIds(item.children, id, [...parents, item.id]);
      if (result.length > 0) return result;
    }
  }

  return [];
};

/**
 * Helper that recursively checks if item exists at any level
 * @param {Array} items - The tree items to search through
 * @param {string} id - The ID to find
 * @returns {boolean} Whether the item exists
 */
export const findItemByIdRecursive = (items, id) => {
  if (!items || !items.length || !id) return false;

  for (const item of items) {
    if (item.id === id) return true;
    if (item.children && item.children.length) {
      if (findItemByIdRecursive(item.children, id)) return true;
    }
  }
  return false;
};

/**
 * Get path to an item as array of titles
 * This is used for logging and debugging
 * @private
 */
export const getItemPath = (items, id, path = []) => {
  if (!items || !items.length || !id) return [];

  for (const item of items) {
    if (item.id === id) {
      return [...path, item.title || item.id];
    }

    if (item.children && item.children.length) {
      const childPath = getItemPath(
        item.children,
        id,
        [...path, item.title || item.id]
      );
      if (childPath.length > 0) return childPath;
    }
  }

  return [];
};

/**
 * Get total count of all items including nested children
 * @param {Array} items - The tree items to search through
 * @returns {number} Total number of items
 */
export const getTotalChildCount = (items) => {
  if (!items || !items.length) return 0;

  let count = items.length;
  for (const item of items) {
    if (item.children && item.children.length) {
      count += getTotalChildCount(item.children);
    }
  }
  return count;
};

// Virtual top-level node id for a grouped project (#125): `project:<projectId>`.
export const projectNodeId = (pid) => `project:${pid}`;
export const isProjectNode = (node) => node && node._kind === 'project';

// #380: lazy levels are paginated so a project with thousands of FLAT items (a
// document-import artefact, #396) can't return an unbounded payload in one request —
// which, with no client timeout, spun forever then surfaced a "network error" as the
// hosted backend timed out / reset the oversized response. We fetch a bounded window and,
// when more remain, append a synthetic `load_more` node the user clicks to fetch the next.
export const TREE_PAGE_SIZE = 500;
export const ROOT_LEVEL_KEY = '__root__';
export const isLoadMoreNode = (node) => node && node._kind === 'load_more';

// ---- pure deep node edits (#567) ---------------------------------------------
// Module scope, not component scope, deliberately: these close over NOTHING, so
// defining them inside the hook produced a fresh function identity on every
// render and made `patchNode`/`removeNode` un-memoizable without suppressing
// exhaustive-deps. Hoisted, their `useCallback([], ...)` arrays are genuinely
// exhaustive. See the design note above `patchNode` for what they are for.
const _replaceNodeDeep = (nodes, id, updater) => {
  if (!nodes || !nodes.length) return { nodes, changed: false };
  let changed = false;
  const out = nodes.map((n) => {
    if (n && n.id === id) { changed = true; return updater(n); }
    if (n && n.children && n.children.length) {
      const res = _replaceNodeDeep(n.children, id, updater);
      if (res.changed) { changed = true; return { ...n, children: res.nodes }; }
    }
    return n;
  });
  return { nodes: changed ? out : nodes, changed };
};

const _removeNodeDeep = (nodes, id) => {
  if (!nodes || !nodes.length) return { nodes, changed: false };
  let changed = false;
  const out = [];
  for (const n of nodes) {
    if (n && n.id === id) { changed = true; continue; }
    if (n && n.children && n.children.length) {
      const res = _removeNodeDeep(n.children, id);
      if (res.changed) { changed = true; out.push({ ...n, children: res.nodes }); continue; }
    }
    out.push(n);
  }
  return { nodes: changed ? out : nodes, changed };
};

// Build the trailing "load more" node for a level, or [] when the level is complete.
// `ctx` = { parentKey, projectId, via } — `via` selects the fetch API on click:
// 'root' (unscoped roots) | 'project' (a project's roots) | 'children' (an item's kids).
export const makeLoadMoreNodes = (page, ctx) => {
  if (!page || !page.has_more) return [];
  const consumed = (page.offset || 0) + (page.returned || 0);
  return [{
    id: `loadmore:${ctx.parentKey}:${page.next_offset}`,
    _kind: 'load_more',
    parentKey: ctx.parentKey,
    projectId: ctx.projectId || null,
    via: ctx.via,
    offset: page.next_offset,
    remaining: Math.max(0, (page.total || 0) - consumed),
    title: 'Load more…',
    has_children: false,
    attributes: { type: 'load_more' },
  }];
};

/**
 * Enhanced tree state hook — LAZY loading edition.
 *
 * The tree no longer fetches the whole forest up front (that was O(N) DB round-trips and a
 * huge payload — unusable at thousands of requirements). Instead it loads ONE level at a
 * time: roots first (each annotated `has_children`), then a node's children on demand when
 * it is expanded. Filtering still spans the whole project via a server-side search that
 * returns matches inside their ancestor scaffolding.
 *
 * Project scope (#125): when the caller can see more than one (selected) project, the top
 * level is a set of project nodes (prefix + name); a single project shows its roots
 * directly; with no projects it falls back to the unscoped roots (legacy/dev).
 *
 * @param {{activeOrg?:string, visibleProjects?:string[], projectPrefix?:string,
 *          searchQuery?:string}} [options]
 */
export const useTreeState = (options = {}) => {
  const {
    activeOrg = null,
    visibleProjects = [],
    projectPrefix = 'PRJ-',
    searchQuery = '',
    includeArchived = false,   // #349: also load soft-deleted (archived) items
    // #350/#428: the review-state filter used to force an EAGER whole-tree load per
    // project so the client-side filter could surface matches at any depth — an
    // O(project) payload per keypress-sized interaction. It now fetches the
    // SERVER-side pruned filter tree (matches + ancestor scaffolding, same shape
    // as search) — pass the active states here.
    reviewFilter = [],
    // Back-compat boolean (older callers): treated as reviewFilter unknown-active.
    reviewFilterActive = false,
    // #817: the item named by the ROUTE (`/item/<id>`), from the caller's router. The
    // hash listener below cannot cover this: react-router navigates with `pushState`,
    // and pushState does not fire `hashchange`. While `/item/:id` rendered its own
    // element that did not matter — the element remounted and read the URL fresh. Now it
    // renders the same tree page, so an in-app navigation to another item changes the URL
    // under a component that is already mounted and hears nothing. #360's notification
    // click is exactly that: it starts on `/`, so the deep-link effect has already run.
    routeItemId = null,
  } = options;
  // F-21/F-22 / REQ-6107/REQ-6108 (#581): RESOLVED (INT-5). `GET /items/review-filter`
  // now evaluates the approval-driven states against the `item_approvals` rollup — the
  // SAME derivation the tree dot uses — instead of the retired free-text status
  // attribute (`core/requirements/project_summary.py::review_filter_match_ids` +
  // `feedback.approval_states_for_items`). While the two disagreed, an unloaded branch
  // could be pruned OUT of a filter that should have matched it, so the server-side
  // eager prefetch silently under-reported.
  //
  // The default set below now sends 'unapproved' (never reviewed) rather than the
  // retired 'unverified'. The server still accepts 'unverified' as a back-compat alias
  // — a client mid-deploy and a bookmarked URL both keep working — but new requests use
  // the name that matches the data model. 'not_approved' (actively rejected) is a
  // separate state and is honoured server-side too.
  const reviewStates = (reviewFilter && reviewFilter.length)
    ? reviewFilter
    : (reviewFilterActive ? ['archive_proposed', 'needs_review', 'unapproved'] : []);
  const eager = reviewStates.length > 0;
  // The tree's "Show archived" toggle widens the filter to archived rows, same
  // rule the client-side RequirementsTree filter applies (#428).
  //
  // `statesKey` is the canonical form: `reviewFilter` arrives as a fresh array
  // literal from the caller on every render, so the *string* is the only stable
  // identity the state set has. `effectiveStates` is then rebuilt FROM the key
  // (state names are fixed identifiers — no commas — so the round-trip is exact),
  // which makes it reference-stable and lets `fetchFilterTree` depend on the real
  // value instead of suppressing the rule with a hand-maintained proxy dep.
  const statesKey = ((eager && includeArchived)
    ? [...reviewStates, 'archived'] : reviewStates).join(',');
  const effectiveStates = useMemo(
    () => (statesKey ? statesKey.split(',') : []),
    [statesKey],
  );
  // #428: fetch the pruned review-filter tree for one project (or unscoped).
  const fetchFilterTree = useCallback(async (projectId) => {
    const res = await requirementsApi.getReviewFilterTree(
      effectiveStates, projectId, { includeArchived });
    return res.roots || [];
  }, [effectiveStates, includeArchived]);

  // Top level (item roots OR project nodes) and the lazily-loaded child levels.
  const [roots, setRoots] = useState([]);
  const [childrenMap, setChildrenMap] = useState({}); // { [parentId]: node[] }
  const [loadingIds, setLoadingIds] = useState(() => new Set()); // nodes whose children are loading
  // Nodes whose child load FAILED. Distinct from "not loading": without this the tree
  // cannot tell an empty container from one whose fetch died, and rendered "…" for both.
  const [failedChildIds, setFailedChildIds] = useState(() => new Set());
  const [searchResult, setSearchResult] = useState(null); // { roots, expand } when a query is active

  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  // Start in `loading` so the window between mount and the (projectsReady-gated)
  // first top-level load reads as "tree loading", not "tree loaded empty" — the
  // grid should only appear once, with real data (#230: e2e waits on the grid's
  // testid, then interacts; a flash of an empty grid let tests interact with a
  // tree that was about to be replaced).
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // The caller's view-permitted projects (fetched once per org); used to resolve scope.
  const [myProjects, setMyProjects] = useState([]);

  // Refs to avoid re-running effects on every keystroke / preserve across renders.
  const loadedRef = useRef(new Set());     // parentIds whose children are loaded
  const loadingRef = useRef(new Set());    // in-flight loads (guards double-fetch)
  const loadSeqRef = useRef(0);            // monotonic id so a stale top-level load can't clobber a newer one
  const urlUpdateEnabledRef = useRef(false);
  const initialUrlHandledRef = useRef(false);
  const selectedRef = useRef([]);          // latest selection for the hashchange listener (avoids stale closure)
  // #917: the item openToItem is currently resolving. The hashchange guard below reads
  // selectedRef, which is written in a post-render effect — so it was never a synchronous
  // guard, and moving setSelected after `await getAncestors` stretched its blind window
  // from one render tick to one network round trip. Within that window a repeat hashchange
  // for the SAME item would start a second open: idempotent, but a duplicate ancestors
  // fetch and a duplicate level load. This ref closes the window synchronously.
  const openingRef = useRef(null);
  // A NEWER OPEN SUPERSEDES AN OLDER ONE. `openingRef` above is per-ITEM by design
  // (#917) — it drops a repeat open of the same id and says nothing about a different
  // one, which the tests below assert deliberately. Two opens for DIFFERENT items do
  // overlap in normal use: the arrival deep link fires `openToItem` on the render that
  // first paints the grid, and an in-app link (RequirementLink, a hashchange, the route
  // effect) can start a second open inside that window. Both await `getAncestors`, and
  // nothing ordered the two `setSelected` calls. Whichever request the server answered
  // LAST therefore won the selection, whatever the user had actually navigated to. The
  // URL mirror then wrote the stale id back into the hash, and the resulting hashchange was
  // swallowed by the `selectedRef` guard, so the wrong item stayed open with no way back
  // to the one that was asked for. Found as the flake at requirement-links.spec.ts:111.
  // Same monotonic-sequence idiom `loadSeqRef` and `searchSeqRef` already use for their
  // own stale-response races.
  const openSeqRef = useRef(0);
  const searchSeqRef = useRef(0);          // monotonic id so a slow/stale search response can't clobber a newer one
  // Bumped by `refreshTree()` so a refresh re-runs the ACTIVE search rather than only
  // reloading `roots`/`childrenMap`, which the view ignores while a query is set (#648).
  const [searchRefreshTick, setSearchRefreshTick] = useState(0);

  // ---- project scope resolution ----------------------------------------
  const effectiveProjects = useMemo(() => {
    if (!myProjects || myProjects.length === 0) return [];
    const sel = Array.isArray(visibleProjects) ? visibleProjects.filter(Boolean) : [];
    const chosen = sel.length > 0 ? myProjects.filter((p) => sel.includes(p.id)) : myProjects;
    return chosen.map((p) => ({ id: p.id, name: p.name || p.title || p.id }));
  }, [myProjects, visibleProjects]);

  const projectKey = effectiveProjects.map((p) => p.id).join(',');
  // Read effectiveProjects inside the search effect via a ref so the effect can depend on
  // the STABLE `projectKey` string, not the array reference. Depending on the array re-fired
  // the search on unrelated re-renders (each response → setSearchResult → re-render → refire),
  // multiplying one keystroke into several concurrent /items/search requests.
  const effectiveProjectsRef = useRef(effectiveProjects);
  useEffect(() => { effectiveProjectsRef.current = effectiveProjects; }, [effectiveProjects]);


  // Gate the first tree load until we know the caller's projects — otherwise the initial
  // unscoped fetch can resolve AFTER the project-scoped one and clobber it (race).
  const [projectsReady, setProjectsReady] = useState(false);

  // Fetch the caller's projects when an org is active (skipped in dev/no-org/tests).
  useEffect(() => {
    let cancelled = false;
    if (!activeOrg) { setMyProjects([]); setProjectsReady(true); return undefined; }
    setProjectsReady(false);
    iamApi.myProjects(activeOrg)
      .then((rows) => { if (!cancelled) setMyProjects(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setMyProjects([]); })
      .finally(() => { if (!cancelled) setProjectsReady(true); });
    return () => { cancelled = true; };
  }, [activeOrg]);

  // #275: `myProjects` above only re-fetches when `activeOrg` CHANGES — creating
  // a project (the tree's "+" -> New Project) doesn't change activeOrg, so
  // without this the brand-new project would never appear (not even after
  // `refreshTree()`, which reloads items but not the project list itself) until
  // a full page reload. Exposed so a caller can re-fetch on demand.
  const reloadMyProjects = useCallback(async () => {
    if (!activeOrg) return;
    try {
      const rows = await iamApi.myProjects(activeOrg);
      setMyProjects(Array.isArray(rows) ? rows : []);
    } catch {
      // non-fatal — the existing project list just stays stale until the next reload
    }
  }, [activeOrg]);

  // ---- URL helpers ------------------------------------------------------
  // #817: the selection can arrive as `#item=<id>` (in-app links) or as the `/item/<id>`
  // path (shared links, the Dashboard Review CTA, notification alerts). Both now land on
  // this page, so both have to be read. See hooks/itemUrl.js for why the path form cannot
  // simply be rewritten into the hash.
  const getSelectedItemFromUrl = useCallback(() => selectedItemFromUrl(window.location), []);

  const updateUrlFromState = useCallback((itemId) => {
    if (!urlUpdateEnabledRef.current) return;
    const next = nextItemUrl(window.location, itemId);
    if (next.pathname !== window.location.pathname) {
      // replaceState, not pushState: the path is where the user ENTERED. Someone who
      // presses Next five times and then Back wants the page they came from, not the
      // fourth item. In-app navigation on the tree page has never pushed history either.
      window.history.replaceState(null, '', `${next.pathname}${next.hash}`);
      return;
    }
    if (next.hash) {
      if (window.location.hash !== next.hash) window.location.hash = next.hash;
      return;
    }
    // Nothing to write. Clear a stale `item=` hash, and ONLY that: on `/item/<id>` the
    // hash may be a `#comment-<id>` anchor a notification linked to, which CommentsTab
    // and BreakdownTab read once their payload loads (#360).
    if (itemIdFromHash(window.location.hash) !== null) window.location.hash = '';
  }, []);

  useEffect(() => {
    if (!urlUpdateEnabledRef.current) return;
    if (selected.length === 1) updateUrlFromState(selected[0]);
    else if (selected.length === 0 && window.location.hash) updateUrlFromState(null);
  }, [selected, updateUrlFromState]);

  // ---- node helpers -----------------------------------------------------
  // Tag a freshly-fetched level so children inherit the project scope (needed for the
  // X-Project-Id on their own lazy fetch) and are flagged as real items.
  const markLevel = useCallback((nodes, projectId) => (
    (nodes || []).map((n) => ({ ...n, _kind: n._kind || 'item', projectId: n.project_id || projectId || null }))
  ), []);

  // Compose the nested tree the UI renders from roots + the lazily-loaded child levels.
  // A lazily-loaded level in `childrenMap` overrides (freshest), but we must ALSO recurse
  // into a node's already-inlined `children` — the single-project node (#380) nests its
  // container list directly on the node (not via childrenMap), so short-circuiting there
  // left every deeper level uncomposed: expanding a container under the lone project loaded
  // its children into childrenMap but they never reached the rendered (inlined) container
  // node, so child rows never appeared (#400).
  const compose = useCallback((node) => {
    const kids = childrenMap[node.id] || node.children;
    if (!kids) return node;
    return { ...node, children: kids.map(compose) };
  }, [childrenMap]);

  // #794: the project prefix is RENDERED, not baked into the title at load time.
  // It used to be composed into `title` when the roots were fetched, which made it a
  // property of the data rather than of the current preference: changing it in
  // Settings -> Display left every project node showing the old prefix until a full
  // page reload. Adding `projectPrefix` to the load effect's deps would have "fixed"
  // that by re-fetching and re-laying-out the whole project forest on a DISPLAY
  // preference — expensive, and it would still leave the prefix baked into whatever
  // came back. Applying it here costs one map over the top level and flows through
  // normal React re-render: no reload, no refetch.
  const withProjectPrefix = useCallback((node) => (
    node?._kind === 'project'
      ? { ...node, title: `${projectPrefix}${node.projectName ?? node.title}` }
      : node
  ), [projectPrefix]);

  const items = useMemo(() => {
    // Both paths build their project group nodes with a RAW `projectName`, so both
    // need the same prefix pass — the search path builds its own group roots.
    if (searchResult) return (searchResult.roots || []).map(withProjectPrefix);
    return (roots || []).map((r) => withProjectPrefix(compose(r)));
    // `compose` already closes over childrenMap, so it's the dependency that matters.
  }, [roots, searchResult, compose, withProjectPrefix]);

  // Keep a ref to the latest composed items for callbacks (deep-link, etc.).
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // ---- top-level load ---------------------------------------------------
  const loadTopLevel = useCallback(async (preserveChildren = false) => {
    const seq = (loadSeqRef.current += 1);
    setLoading(true);
    // #380: id of the single-project node to seed default-open (null otherwise).
    let singleProjectNodeId = null;
    // #380: child levels to seed into childrenMap after the reset (single-project roots).
    let pendingChildLevels = null;
    try {
      let nextRoots;
      if (effectiveProjects.length > 1) {
        if (eager) {
          // #350/#428: filter active — fetch each visible project's SERVER-pruned
          // filter tree (matches + ancestors, not the full tree), nesting each
          // under its project group node (which renders auto-expanded while filtering).
          nextRoots = await Promise.all(effectiveProjects.map(async (p) => ({
            id: projectNodeId(p.id),
            projectId: p.id,
            _kind: 'project',
            title: p.name, projectName: p.name,
            has_children: true,
            attributes: { type: 'container' },
            children: markLevel(await fetchFilterTree(p.id), p.id),
          })));
        } else {
          // Group: one virtual node per project; each lazy-loads its own roots.
          nextRoots = effectiveProjects.map((p) => ({
            id: projectNodeId(p.id),
            projectId: p.id,
            _kind: 'project',
            title: p.name, projectName: p.name,
            has_children: true,
            attributes: { type: 'container' },
          }));
        }
      } else if (effectiveProjects.length === 1) {
        // #380: always surface the project as a top-level node — even with a single
        // project — so its project-scoped context-menu actions and views (export,
        // archive, copy, new item…) stay reachable (previously the lone project's
        // items were hoisted to the root with no project node, hiding those). It's
        // seeded default-OPEN (see below) so the single project doesn't cost a click.
        const p = effectiveProjects[0];
        const key = projectNodeId(p.id);
        const projectNode = {
          id: key,
          projectId: p.id,
          _kind: 'project',
          title: p.name, projectName: p.name,
          has_children: true,
          attributes: { type: 'container' },
        };
        if (eager) {
          // Review-filter mode (#428): nest the server-pruned filter tree —
          // matches at any depth inside their ancestor scaffolding, not the
          // whole unbounded tree.
          const data = await fetchFilterTree(p.id);
          nextRoots = [{ ...projectNode, children: markLevel(data, p.id) }];
        } else {
          // #380: fetch a BOUNDED first page of roots and hold them in childrenMap (not
          // nested on the node) so "load more" can append the next page. Appends a
          // load_more node when the project has more roots than one page.
          const { items: pageItems, page } = await requirementsApi.getItems(
            false, p.id, { includeArchived, paged: true, limit: TREE_PAGE_SIZE });
          const level = [
            ...markLevel(pageItems, p.id),
            ...makeLoadMoreNodes(page, { parentKey: key, projectId: p.id, via: 'project' }),
          ];
          nextRoots = [projectNode];
          pendingChildLevels = { [key]: level };
        }
        singleProjectNodeId = key;
      } else if (eager) {
        // Unscoped review-filter mode (#428): server-pruned, like the scoped case.
        const data = await fetchFilterTree(null);
        nextRoots = markLevel(data, null);
      } else {
        // #380: bounded first page of the unscoped root level (legacy/dev, no projects).
        const { items: pageItems, page } = await requirementsApi.getItems(
          false, null, { includeArchived, paged: true, limit: TREE_PAGE_SIZE });
        nextRoots = [
          ...markLevel(pageItems, null),
          ...makeLoadMoreNodes(page, { parentKey: ROOT_LEVEL_KEY, projectId: null, via: 'root' }),
        ];
      }
      // A newer load started while we awaited — its result wins; drop ours.
      if (loadSeqRef.current !== seq) return;
      setRoots(nextRoots);
      if (!preserveChildren) {
        setChildrenMap(pendingChildLevels || {});
        loadedRef.current = new Set();
      } else if (pendingChildLevels) {
        setChildrenMap((prev) => ({ ...prev, ...pendingChildLevels }));
      }
      if (singleProjectNodeId) {
        // The single project's roots live in childrenMap (or are nested directly in
        // eager mode), so mark it loaded (don't let a lazy expand refetch/override
        // them) and seed it into `expanded` so it renders open on first paint. (#380)
        loadedRef.current.add(singleProjectNodeId);
        setExpanded((prev) => (prev.includes(singleProjectNodeId) ? prev : [...prev, singleProjectNodeId]));
      }
      setError(null);
    } catch (err) {
      if (loadSeqRef.current !== seq) return;
      console.error('Failed to load items:', err);
      setError(err.message || 'Failed to load items');
      setRoots([]);
    } finally {
      if (loadSeqRef.current === seq) setLoading(false);
      if (!urlUpdateEnabledRef.current) urlUpdateEnabledRef.current = true;
    }
  }, [effectiveProjects, markLevel, includeArchived, eager, fetchFilterTree]);

  // ---- lazy child load --------------------------------------------------
  // Accepts a node object (preferred — carries projectId/_kind) or a bare id.
  const loadChildren = useCallback(async (nodeOrId) => {
    const node = typeof nodeOrId === 'string'
      ? (findItemById(itemsRef.current, nodeOrId) || { id: nodeOrId })
      : nodeOrId;
    const id = node.id;
    if (!id || searchResult) return; // search mode is fully materialised already
    if (loadedRef.current.has(id) || loadingRef.current.has(id)) return;

    loadingRef.current.add(id);
    setLoadingIds(new Set(loadingRef.current));
    // Clear any previous failure for this node up front, so a retry shows "Loading…"
    // rather than the stale failure text while the new request is in flight.
    setFailedChildIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    try {
      // #380: fetch a BOUNDED first page and, when more remain, append a load_more node
      // so a node with thousands of flat children (a doc-import artefact, #396) never
      // pulls its whole child level in one unbounded response.
      let pageItems; let page; let via;
      if (node._kind === 'project') {
        via = 'project';
        ({ items: pageItems, page } = await requirementsApi.getItems(
          false, node.projectId, { includeArchived, paged: true, limit: TREE_PAGE_SIZE }));
      } else {
        via = 'children';
        ({ items: pageItems, page } = await requirementsApi.getChildren(
          id, { projectId: node.projectId, includeArchived, paged: true, limit: TREE_PAGE_SIZE }));
      }
      const level = [
        ...markLevel(pageItems, node.projectId),
        ...makeLoadMoreNodes(page, { parentKey: id, projectId: node.projectId, via }),
      ];
      setChildrenMap((prev) => ({ ...prev, [id]: level }));
      loadedRef.current.add(id);
    } catch (err) {
      console.error(`Failed to load children for ${id}:`, err);
      showSnackbarRef.current(`Failed to load children: ${err.message}`, 'error');
      // Owner report, 2026-07-28: "if the tree view fails to load, it needs to be more
      // explicit 'Failed to load' with a refresh button".
      //
      // Before this, a failed child load left NO durable trace on the tree. The branch
      // keeps its placeholder row, but the id is now in neither `loadingRef` nor
      // `loadedRef`, so that row renders a bare "…" — indefinitely, and indistinguishable
      // from "this container is empty". The only signal was a snackbar that had already
      // gone by the time the user looked. Recording the failure is what lets the row say
      // what happened and offer a retry.
      setFailedChildIds((prev) => new Set(prev).add(id));
    } finally {
      loadingRef.current.delete(id);
      setLoadingIds(new Set(loadingRef.current));
    }
  }, [searchResult, markLevel, includeArchived]);

  // Retry a branch whose child load failed. `loadChildren` already early-returns only
  // for ids that are loaded or in flight — a failed id is in neither — so this needs no
  // special path beyond being a stable, nameable callback for the retry button.
  const retryChildren = useCallback((nodeOrId) => loadChildren(nodeOrId), [loadChildren]);

  // ---- lazy "load more" for a paginated level (#380) --------------------
  // Fetches the NEXT page for the level the given load_more node terminates, then splices
  // the new items in place of that node (re-appending a fresh load_more if more remain).
  const loadMore = useCallback(async (node) => {
    if (!isLoadMoreNode(node) || searchResult) return;
    const guard = node.id;
    if (loadingRef.current.has(guard)) return;
    loadingRef.current.add(guard);
    setLoadingIds(new Set(loadingRef.current));
    try {
      let pageItems; let page;
      const opts = { includeArchived, paged: true, limit: TREE_PAGE_SIZE, offset: node.offset };
      if (node.via === 'children') {
        ({ items: pageItems, page } = await requirementsApi.getChildren(
          node.parentKey, { projectId: node.projectId, ...opts }));
      } else {
        // 'root' -> unscoped roots; 'project' -> a project's roots.
        ({ items: pageItems, page } = await requirementsApi.getItems(
          false, node.via === 'project' ? node.projectId : null, opts));
      }
      const more = markLevel(pageItems, node.projectId);
      const tail = makeLoadMoreNodes(page, {
        parentKey: node.parentKey, projectId: node.projectId, via: node.via,
      });
      const splice = (list) => [...(list || []).filter((n) => !isLoadMoreNode(n)), ...more, ...tail];
      if (node.parentKey === ROOT_LEVEL_KEY) {
        setRoots((prev) => splice(prev));
      } else {
        setChildrenMap((prev) => ({ ...prev, [node.parentKey]: splice(prev[node.parentKey]) }));
      }
    } catch (err) {
      console.error(`Failed to load more for ${node.parentKey}:`, err);
      showSnackbarRef.current(`Failed to load more: ${err.message}`, 'error');
    } finally {
      loadingRef.current.delete(guard);
      setLoadingIds(new Set(loadingRef.current));
    }
  }, [searchResult, markLevel, includeArchived]);

  // ---- server-side search ----------------------------------------------
  // `searchQuery` is already debounced by the caller (RequirementsTreeView), so this fires
  // ~once per settled word. The seq guard is the correctness backstop: if a later query's
  // request resolves before an earlier one, only the newest may write state (latest-wins) —
  // otherwise a slow stale response overwrites fresh results ("same query, different data").
  useEffect(() => {
    const q = (searchQuery || '').trim();
    // Bump the seq even when clearing, so any in-flight request from a prior query is
    // invalidated and can't set results after we've cleared them.
    const mySeq = (searchSeqRef.current += 1);
    if (!q) { setSearchResult(null); return undefined; }
    const isLatest = () => searchSeqRef.current === mySeq;
    const projects = effectiveProjectsRef.current;
    (async () => {
      try {
        if (projects.length > 1) {
          // ONE tenant-wide request (no project scope) — the backend already searches every
          // project the caller can see. We then group the returned roots by their project_id
          // client-side. (Previously this fanned out to one /items/search PER project, which
          // spammed the API with N concurrent requests per word for a multi-project user.)
          const res = await requirementsApi.searchItems(q);
          if (!isLatest()) return;
          const visible = new Map(projects.map((p) => [p.id, p.name]));
          const byProject = new Map();
          (res.roots || []).forEach((root) => {
            const pid = root.projectId || root.project_id;
            if (!visible.has(pid)) return; // respect the user's visible-projects scope
            if (!byProject.has(pid)) byProject.set(pid, []);
            byProject.get(pid).push(root);
          });
          const groupRoots = [...byProject.entries()].map(([pid, rs]) => ({
            id: projectNodeId(pid), projectId: pid, _kind: 'project',
            title: visible.get(pid) || pid, projectName: visible.get(pid) || pid,
            has_children: true,
            attributes: { type: 'container' }, children: rs,
          }));
          const expand = [
            ...(res.expand || []),
            ...[...byProject.keys()].map(projectNodeId),
          ];
          setSearchResult({ roots: groupRoots, expand });
        } else {
          const pid = projects.length === 1 ? projects[0].id : null;
          const res = await requirementsApi.searchItems(q, pid);
          if (!isLatest()) return;
          setSearchResult({ roots: res.roots || [], expand: res.expand || [] });
        }
      } catch (err) {
        if (!isLatest()) return;
        console.error('Search failed:', err);
        setSearchResult({ roots: [], expand: [] });
      }
    })();
    // Depend on the STABLE projectKey string (not the effectiveProjects array) so unrelated
    // re-renders don't re-fire the search; effectiveProjects is read via its ref above.
    //
    // `searchRefreshTick` (#648) is what makes `refreshTree()` mean anything while a
    // search is active. `items` returns `searchResult.roots` whenever a query is set,
    // so a refresh that only reloads `roots`/`childrenMap` is reloading data the view
    // is not looking at — the tree keeps rendering the OLD search response.
    // Caught by the non-drag reorder e2e: the PUT landed, the server order changed,
    // and the rows on screen did not move. From the user's side that is
    // indistinguishable from the control not working, so they press it again.
  }, [searchQuery, projectKey, searchRefreshTick]);

  // When a search resolves, auto-expand the ancestor branches so matches are visible.
  useEffect(() => {
    if (searchResult && searchResult.expand && searchResult.expand.length) {
      setExpanded((prev) => Array.from(new Set([...prev, ...searchResult.expand])));
    }
  }, [searchResult]);

  // ---- initial + project-change load -----------------------------------
  // Wait for projectsReady so the first load is already correctly scoped (no unscoped
  // pre-load that could resolve late and clobber the project-scoped tree).
  //
  // The dependency array below is the deliberate TRIGGER CONTRACT — the exact set of
  // things that mean "the tree's scope changed, reload it" (statesKey: switching WHICH
  // review states are filtered refetches the pruned tree, #428, not just toggling the
  // filter on/off). `loadTopLevel` is deliberately NOT one of them: it is a fat
  // useCallback over `effectiveProjects`/`fetchFilterTree`/`projectPrefix`, and listing
  // it would make the tree's whole-forest reload fire on any identity churn in that
  // chain — the classic self-feeding reload loop, on the one effect in the app that
  // issues the most requests. Read the latest one through a ref instead: same function
  // that would have been called, without letting its identity become a trigger.
  const loadTopLevelRef = useRef(loadTopLevel);
  useEffect(() => { loadTopLevelRef.current = loadTopLevel; }, [loadTopLevel]);
  useEffect(() => {
    if (!projectsReady) return;
    loadTopLevelRef.current(false);
  }, [projectKey, projectsReady, includeArchived, eager, statesKey]);

  // Open the branch down to `itemId`: select it, then load + expand each ancestor level
  // so a lazily-loaded deep item becomes visible. Used by both the initial deep-link and
  // the runtime hashchange (in-app navigation), so neither needs a full reload.
  const openToItem = useCallback(async (itemId) => {
    if (!itemId) return;
    // Set BEFORE the first await, cleared in `finally`, so a repeat call for the same item
    // while this one is still resolving is dropped rather than duplicated.
    if (openingRef.current === itemId) return;
    openingRef.current = itemId;
    // Claim a sequence number BEFORE the first await — see `openSeqRef` above. Every
    // state write below is guarded on still holding the newest one.
    const seq = (openSeqRef.current += 1);
    try {
      // #917: resolve the item's project BEFORE selecting it. Selecting is what makes
      // ItemDetails fetch, and a deep link names the item but not its project — so with
      // the old order (select, then await getAncestors) the detail request went out
      // carrying the viewer's stored project and 404'd into a permanent "Error loading
      // item", since nothing re-fetches. That ordering is the race #917 observed on the
      // e2e stack: pass or fail depended on which of the two requests won.
      //
      // getAncestors resolves the real project, so record it here and let the item's own
      // requests (detail, history, comment count, breakdown, approval trail) scope
      // themselves to it. The viewer's stored selection is not touched.
      //
      // The cost is that the row highlights one round trip later. getAncestors was
      // already on the critical path for revealing the row, so this adds no request.
      // `unscoped`: the project is exactly what this call has to discover, and the
      // scoped form cannot answer it for an item outside the current selection.
      const { ancestors, projectId } = await requirementsApi.getAncestors(itemId, { unscoped: true });
      // Recorded even when this open has been superseded: an id→project fact cannot go
      // stale, and a later return to this item then skips the wrong-project 404 #917 fixed.
      noteItemProject(itemId, projectId);
      // Superseded — a newer open is already resolving, and it owns the selection now.
      // Returning here also drops this open's ancestor loads, which the newer one is
      // issuing for its own branch anyway.
      if (openSeqRef.current !== seq) return;
      setSelected([itemId]);
      // #396/#380: in a MULTI-project org the item's real ancestors sit UNDER a synthetic
      // `project:<id>` group node that getAncestors (real items only) never returns. That
      // group node is collapsed by default (unlike the single-project node, which is
      // seeded open with its roots), so without prepending + loading it the topmost real
      // ancestor is a child of an unopened node and never renders — the deep link opens
      // to nothing. Prepend it so the whole branch (project → containers → item) opens.
      // NOT switching the active project here, deliberately — see #917. Following the
      // item into its own project fixes its 404 and causes a worse one: chooseProject
      // PERSISTS to localStorage, so the switch outlives the deep link and every later
      // request in that session carries the new project. Measured: dashboard-breakdown
      // went red with twelve console 404s on /items/<id>/breakdown and
      // /items/<id>/approval-trail for items in the project the viewer had left.
      const chain = (effectiveProjectsRef.current.length > 1 && projectId)
        ? [projectNodeId(projectId), ...ancestors]
        : ancestors;
      for (const aid of chain) {
         
        await loadChildren(aid);
      }
      setExpanded((prev) => Array.from(new Set([...prev, ...chain])));
    } catch (err) {
      console.error('Open-to-item failed:', err);
      // Superseded, same rule as the success path: a FAILED older open must not pull the
      // selection off the item a newer one is opening.
      if (openSeqRef.current !== seq) return;
      // Selecting used to happen before this call could throw. Keep that: a failure to
      // resolve ancestors should still select the item (the panel then reports its own
      // error), not silently leave the deep link pointing at nothing.
      setSelected([itemId]);
    } finally {
      // Only clear our own claim: a LATER open for a different item has already taken the
      // ref, and clearing unconditionally would let this one cancel that one's guard.
      if (openingRef.current === itemId) openingRef.current = null;
    }
  }, [loadChildren]);

  // Deep-link: once the top level is loaded, open the branch to the URL-selected item.
  // `initialUrlHandledRef` makes this idempotent — every run after the first returns at
  // the guard — so the real deps can be listed rather than suppressed: `openToItem`
  // changing (it tracks `loadChildren`, which tracks `searchResult`) just re-enters and
  // bails, it cannot re-open the deep link a second time.
  useEffect(() => {
    if (initialUrlHandledRef.current || loading || !projectsReady) return;
    initialUrlHandledRef.current = true;
    const urlItem = getSelectedItemFromUrl();
    if (urlItem) openToItem(urlItem);
  }, [loading, projectsReady, getSelectedItemFromUrl, openToItem]);

  // #817: the ROUTE's item, for the same reason the hash listener exists — and covering
  // what it cannot (see `routeItemId` above). Guarded on the current selection so it is
  // idempotent: `openToItem`'s identity tracks `loadChildren`, so this effect re-enters
  // on unrelated churn and must be safe to.
  const handledRouteItemRef = useRef(null);
  useEffect(() => {
    if (!routeItemId) return;
    // ONCE PER DISTINCT ROUTE ID. Not "whenever the selection disagrees": after an in-page
    // move (a Prev/Next click, a swipe) the selection legitimately differs from the item
    // the route named, and re-opening then drags the user back to where they arrived.
    if (handledRouteItemRef.current === routeItemId) return;
    handledRouteItemRef.current = routeItemId;
    if (selectedRef.current.includes(routeItemId)) return;
    openToItem(routeItemId);
  }, [routeItemId, openToItem]);

  // In-app navigation: react to browser hash changes (a RequirementLink / back-forward
  // sets `#item=<id>`) by opening to that item — without a full tree reload. Guarded so
  // selecting a row (which writes the hash) doesn't redundantly re-open it.
  useEffect(() => {
    const onHashChange = () => {
      const urlItem = getSelectedItemFromUrl();
      if (urlItem) {
        if (!selectedRef.current.includes(urlItem)) openToItem(urlItem);
      } else {
        setSelected([]);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [openToItem, getSelectedItemFromUrl]);

  // ---- refresh ----------------------------------------------------------
  // Reload the top level and re-fetch any currently-expanded branch so an edit/refresh
  // doesn't collapse the user's open tree.
  const refreshTree = useCallback(async () => {
    const openIds = [...expanded];
    setChildrenMap({});
    loadedRef.current = new Set();
    // Re-run the active search too. Without this the reload below is invisible
    // whenever a query is set, because `items` renders `searchResult.roots` and
    // nothing here touches that. No-op when no search is active — the effect
    // short-circuits on an empty query.
    setSearchRefreshTick((n) => n + 1);
    await loadTopLevel(true);
    // Re-load expanded branches breadth-first (parents before children).
    for (const id of openIds) {
       
      await loadChildren(id);
    }
  }, [expanded, loadTopLevel, loadChildren]);

  // ---- optimistic single-node updates (#567) -----------------------------
  // `refreshTree()` above reloads the WHOLE top level + every expanded branch —
  // right for a structural change (create/move/delete a DIFFERENT node), but a
  // full multi-request reload for every Save/autosave/approve of the CURRENTLY
  // open item is what produced the reported "tree reloads continuously" / "reloads
  // the whole subtree on Verify/Save/Delete" (#567): ItemDetails called
  // `refreshTree()` unconditionally from handleSave/handleAutosaved/
  // handleToggleVerified/handleDeleteConfirm. These two helpers patch/remove
  // exactly the ONE affected node in `roots`/`childrenMap` in place — no network
  // round trip at all. A node can live either directly in `roots`/`childrenMap`
  // (the normal lazy-loaded path) or inlined on a container's own `.children`
  // (the eager/review-filter/single-project-nested shapes `compose()` already
  // has to special-case, see its comment above) — `_replaceNodeDeep`/
  // `_removeNodeDeep` (module scope, top of file) recurse into both so the patch
  // finds the node wherever it actually lives.

  // Patch ONE node's fields in place (e.g. a title rename from Save) without
  // touching any other node or firing a single network request. A no-op
  // (returns unchanged state) if `id` isn't currently loaded anywhere — the
  // caller just missed a redundant reload, not an error.
  const patchNode = useCallback((id, updater) => {
    setRoots((prev) => {
      const res = _replaceNodeDeep(prev, id, updater);
      return res.changed ? res.nodes : prev;
    });
    setChildrenMap((prev) => {
      let any = false;
      const next = {};
      for (const [key, arr] of Object.entries(prev)) {
        const res = _replaceNodeDeep(arr, id, updater);
        next[key] = res.nodes;
        if (res.changed) any = true;
      }
      return any ? next : prev;
    });
  }, []);

  // Remove ONE node (Delete/confirm-archive) from wherever it's loaded, plus its
  // own now-orphaned child level (if any was loaded into childrenMap) and any
  // stale selected/expanded/loaded bookkeeping that referenced it.
  const removeNode = useCallback((id) => {
    setRoots((prev) => {
      const res = _removeNodeDeep(prev, id);
      return res.changed ? res.nodes : prev;
    });
    setChildrenMap((prev) => {
      let any = id in prev;
      const next = {};
      for (const [key, arr] of Object.entries(prev)) {
        if (key === id) { any = true; continue; } // the deleted node's own child level
        const res = _removeNodeDeep(arr, id);
        next[key] = res.nodes;
        if (res.changed) any = true;
      }
      return any ? next : prev;
    });
    setSelected((prev) => prev.filter((x) => x !== id));
    setExpanded((prev) => prev.filter((x) => x !== id));
    loadedRef.current.delete(id);
  }, []);

  // ---- snackbar ---------------------------------------------------------
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const showSnackbarRef = useRef(showSnackbar);
  useEffect(() => { showSnackbarRef.current = showSnackbar; }, [showSnackbar]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // ---- CRUD -------------------------------------------------------------
  // #197 fix: accepts the FULL item payload AddDialog builds (title, description,
  // parent_id, attributes, and an optional custom `type`/`type_id`) instead of just
  // (parentId, title, description) — the old 3-arg shape silently dropped
  // `attributes.type`/`type`/`type_id` before they ever reached the API, so
  // creating a CONTAINER (or any custom-typed item) via the tree's Add dialog
  // always landed as a plain system requirement regardless of what was selected.
  // Still accepts the legacy 3-positional-arg call (parentId, title, description)
  // for any other caller that hasn't been updated to pass the full object.
  const addNewItem = useCallback(async (itemOrParentId, maybeTitle, maybeDescription) => {
    const itemData = (itemOrParentId && typeof itemOrParentId === 'object')
      ? itemOrParentId
      : { parent_id: itemOrParentId, title: maybeTitle, description: maybeDescription };
    const title = itemData.title;
    try {
      setLoading(true);
      await requirementsApi.createItem(itemData);
      await refreshTree();
      showSnackbar(`Item "${title}" created successfully`, 'success');
    } catch (err) {
      console.error('Failed to create item:', err);
      setError('Failed to create new item: ' + err.message);
      showSnackbar(`Failed to create item: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [refreshTree, showSnackbar]);

  const deleteItem = useCallback(async (itemId) => {
    try {
      setLoading(true);
      await requirementsApi.deleteItem(itemId);
      if (selected.includes(itemId)) setSelected(selected.filter((id) => id !== itemId));
      await refreshTree();
      showSnackbar('Item deleted successfully', 'success');
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError('Failed to delete item: ' + err.message);
      showSnackbar(`Failed to delete item: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [refreshTree, selected, showSnackbar]);

  const deleteMultipleItems = useCallback(async (itemIds) => {
    if (!itemIds || itemIds.length === 0) return;
    setLoading(true);
    try {
      const result = await requirementsApi.deleteMultipleItems(itemIds);
      const newSelected = selected.filter((id) => !result.deleted.includes(id));
      setSelected(newSelected);
      if (result.failed.length > 0) {
        showSnackbar(`Deleted ${result.deleted.length} items. Failed to delete ${result.failed.length} items.`, 'warning');
      } else {
        showSnackbar(`Successfully deleted ${result.deleted.length} items.`, 'success');
      }
      await refreshTree();
    } catch (err) {
      console.error('Failed to delete items:', err);
      setError('Failed to delete items: ' + err.message);
      showSnackbar(`Failed to delete items: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [refreshTree, selected, showSnackbar]);

  const updateItemFields = useCallback(async (itemId, patch) => {
    try {
      // #572: PUT now requires a `version` precondition. A caller that already
      // knows the current version can pass it in `patch`; otherwise fetch it
      // fresh so this generic patch helper never silently 428s.
      const version = patch && patch.version != null
        ? patch.version
        : (await requirementsApi.getItem(itemId)).version;
      // #579: a real concurrent edit here used to surface as "Failed to update item:
      // Request failed with status code 409" — accurate and useless. Route it through
      // the shared reload / keep-mine prompt instead; the refreshTree() below then runs
      // for BOTH outcomes (theirs reloaded, or mine re-applied over the top).
      const res = await saveWithConflictPrompt(itemId, { ...patch, version });
      await refreshTree();
      if (res.conflict) showSnackbar('Reloaded the newer version of that item', 'info');
    } catch (err) {
      console.error('Failed to update item:', err);
      showSnackbar(`Failed to update item: ${err.message}`, 'error');
    }
  }, [refreshTree, showSnackbar]);

  const moveItem = useCallback(async (itemId, payload) => {
    try {
      await requirementsApi.moveItem(itemId, payload);
      await refreshTree();
    } catch (err) {
      console.error('Failed to move item:', err);
      showSnackbar(`Failed to move item: ${err.message}`, 'error');
    }
  }, [refreshTree, showSnackbar]);

  const decomposeItem = useCallback(async (itemId) => {
    try {
      setLoading(true);
      await requirementsApi.decomposeRequirement(itemId);
      await refreshTree();
      showSnackbar('Item decomposed successfully', 'success');
    } catch (err) {
      console.error('Failed to decompose item:', err);
      setError('Failed to decompose item: ' + err.message);
      showSnackbar(`Failed to decompose item: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [refreshTree, showSnackbar]);

  return {
    // Data and state
    items,
    roots,
    expanded,
    setExpanded,
    selected,
    setSelected,
    loading,
    error,
    snackbar,
    loadingIds,
    failedChildIds,
    retryChildren,
    searchActive: Boolean(searchResult),
    // The caller's view-permitted projects (#197) — independent of the current
    // search/filter state (unlike `items`, which can be filtered down to zero
    // rows), so a caller can reliably resolve "is there exactly one project in
    // scope" for a top-level Add even while the tree is search-filtered empty.
    myProjects,
    reloadMyProjects,

    // URL hash and navigation
    getSelectedItemFromUrl,
    updateUrlFromState,

    // Tree operations
    loadTree: loadTopLevel,
    refreshTree,
    loadChildren,
    loadMore,
    // #567: optimistic single-node update/remove — see the definitions above.
    patchNode,
    removeNode,

    // Tree utilities
    findItemById,
    findParentIds,
    findItemByIdRecursive,
    getItemPath,
    getTotalChildCount,

    // CRUD operations
    addNewItem,
    deleteItem,
    deleteMultipleItems,
    decomposeItem,
    updateItemFields,
    moveItem,

    // UI feedback
    showSnackbar,
    handleCloseSnackbar,
  };
};
